import { router, usePage } from '@inertiajs/react'
import { useEffect, useState, type ReactNode } from 'react'
import type { SharedProps } from '@/types'
import { ToastStack, CelebrationCard } from '@/components/Celebrate'
import { ToastHost } from '@/components/Toast'
import { SpinWheel } from '@/components/Spin'
import { Sheet, SheetRow, SheetDivider } from '@/components/Sheet'
import Avatar from '@/components/Avatar'
import TabBar from '@/components/TabBar'
import TopBar, { type TopBarConfig } from '@/components/TopBar'
import { t } from '@/lib/i18n'
import { coin, isMuted, setMuted } from '@/lib/sound'

/**
 * 登录后的应用外壳：顶栏（52px）+ 内容 + 底部 tab bar（56px + 安全区）。
 *
 * 营销页 / 登录页 / 法务页**不用**这个壳（它们各有 MarketingHeader / AuthShell），
 * 所以底部那 68px 的留白挂在这里的容器上（pb-shell）而不是 body 上。
 *
 * `bleed`：内容区不要左右内边距，页面自己用 `.sect` 的 mx-3 管边距。
 * S2/S3 重做的页面（打卡 / 目标 / 商店 / 看板）用它；还没重做的那几页
 * （流水 / 成就 / 人员 / 账号）继续用默认的居中限宽容器。
 */
export default function Layout({ children, topbar, bleed = false }: {
  children: ReactNode
  topbar?: TopBarConfig
  bleed?: boolean
}) {
  const { people, current_person, current_user, flash } = usePage<SharedProps>().props
  const [sheet, setSheet] = useState<'more' | 'person' | null>(null)
  const close = () => setSheet(null)

  // 在 effect 里读 localStorage 而不是在渲染里：首帧一律按「有声」渲染，
  // 不依赖任何浏览器存储是否可用（隐私模式下 isMuted() 恒为 false，正好一致）。
  const [muted, setMutedUi] = useState(false)
  useEffect(() => setMutedUi(isMuted()), [])

  const toggleSound = () => {
    const next = !muted
    setMuted(next)
    setMutedUi(next)
    // 取消静音时立刻响一声：既是确认，也顺便在这次点击的手势里把 AudioContext 建起来。
    if (!next) coin()
  }

  return (
    <div className="min-h-screen pb-shell">
      <TopBar {...topbar} onPerson={() => setSheet('person')} />

      <ToastHost />
      <ToastStack />
      <CelebrationCard />
      <SpinWheel />

      <main className={bleed ? 'max-w-[560px] mx-auto' : 'max-w-[560px] mx-auto px-3 py-3 grid gap-3'}>
        {flash?.notice && (
          <div className="text-sm rounded-chip px-3 py-2 bg-green-tint text-green-strong border border-green-line">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="text-sm rounded-chip px-3 py-2 bg-red-tint text-red-strong border border-red-line">
            {flash.alert}
          </div>
        )}
        {children}
      </main>

      <TabBar onMore={() => setSheet('more')} />

      {/* ── 「我的」sheet：七项各自都够不上一个 tab，但都得有地方去 ── */}
      <Sheet open={sheet === 'more'} onClose={close} label={t('layout.tabs.mine')} title={t('layout.tabs.mine')}>
        <SheetRow icon="🏅" href="/badges" onClick={close}>{t('layout.badges')}</SheetRow>
        <SheetRow icon="📒" href="/ledger" onClick={close}>{t('layout.ledger')}</SheetRow>
        {/* 相册紧挨流水：两者都是「回看过去发生过什么」——流水回看积分，相册回看证据。
            不放作业页顶栏：那里已经四样东西（‹ + 人员 + 余额 + ⚙️），而相册是
            偶尔回看、不是每天要点的（同静音开关的处理，差距审计 §5③）。 */}
        <SheetRow icon="🖼️" href="/homework/gallery" onClick={close}>{t('homework.gallery')}</SheetRow>
        <SheetDivider />
        <SheetRow icon="👥" href="/people" onClick={close} tail={t('people.count', { count: people.length })}>
          {t('layout.people')}
        </SheetRow>
        {current_user && (
          <SheetRow icon="⚙️" href="/account/edit" onClick={close} tail={current_user.email_address}>
            {t('layout.account')}
          </SheetRow>
        )}
        {/* 订阅：稿子的「我的」sheet 里没有这一行，但仓库有 /pricing 与 Entitlement，
            而付费墙那一刀（docs/superpowers/specs/2026-08-27-paywall-design.md）要建两道墙，
            墙的提示界面就是 sheet。现在把位置留好，那一片不用再改壳。
            刻意不显示当前档位：那要往 inertia_share 里加一次 exists? 查询，
            而这一片是纯 UI，不动共享 props。 */}
        <SheetRow icon="⭐" href="/pricing" onClick={close}>{t('layout.subscription')}</SheetRow>
        <SheetDivider />
        {/* 静音从顶栏挪进来。**不关 sheet**：连点两下试听是合理操作。 */}
        <SheetRow
          icon={muted ? '🔇' : '🔊'}
          onClick={toggleSound}
          tail={muted ? t('layout.sound_off') : t('layout.sound_on')}
        >
          {t('layout.sound')}
        </SheetRow>
        {current_user && (
          <>
            <SheetDivider />
            <SheetRow icon="↩" tone="danger" onClick={() => { close(); router.delete('/session') }}>
              {t('layout.sign_out')}
            </SheetRow>
          </>
        )}
      </Sheet>

      {/* ── 人员切换 sheet：取代原来右上角的 dropdown（手机上够不着屏幕右上角） ── */}
      <Sheet open={sheet === 'person'} onClose={close} label={t('layout.switch_person')} title={t('layout.switch_person')}>
        {people.map((p) => {
          const active = p.id === current_person?.id
          return (
            <SheetRow
              key={p.id}
              onClick={() => { close(); if (!active) router.post(`/people/${p.id}/switch`) }}
              tail={active ? <span className="text-brand">✓ {t('people.current')}</span> : undefined}
            >
              <span className="flex items-center gap-3">
                <Avatar name={p.name} url={p.avatar_url} />
                {p.name}
              </span>
            </SheetRow>
          )
        })}
        <SheetDivider />
        <SheetRow icon="➕" href="/people" onClick={close}>{t('layout.add_person')}</SheetRow>
      </Sheet>
    </div>
  )
}

export function Card({ title, extra, children }: { title?: ReactNode; extra?: ReactNode; children: ReactNode }) {
  // 稿子的 `.card` 只有 shadow-card，没有 border —— 两者同时在，卡片边缘看起来是双线。
  return (
    <section className="bg-card rounded-card shadow-card">
      {title && (
        <div className="px-[18px] py-[15px] border-b border-line flex items-center gap-2.5">
          <h2 className="text-[15px] font-bold m-0">{title}</h2>
          <div className="flex-1" />
          {extra}
        </div>
      )}
      <div className="px-[18px] py-4">{children}</div>
    </section>
  )
}

/**
 * 稿子的 `.emptystate`：大 emoji + 一句话 + **一个出口**。
 *
 * 原来是一行灰字。空态是新用户见到的第一屏，一行灰字既不说明这是什么，
 * 也没告诉人下一步点哪 —— 那三样缺一样都算没做完。
 */
export function Empty({ emoji, title, children, action }: {
  emoji: string
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mx-3 mt-3 px-5 py-[34px] text-center bg-card rounded-card shadow-card">
      <div className="text-[44px] leading-none">{emoji}</div>
      <h3 className="mt-2.5 mb-1 text-[17px] font-bold">{title}</h3>
      {children && <p className="m-0 text-muted text-[13px]">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}
